import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);
const VIEW_AS_KEY = 'bq_view_as';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Admin-only preview toggle: when set, storefront pricing uses this tier
  // instead of the admin's real user_type. Persisted so it survives refreshes.
  const [viewAsType, setViewAsTypeState] = useState(() => {
    try { return localStorage.getItem(VIEW_AS_KEY) || null; } catch { return null; }
  });

  const setViewAsType = useCallback((type) => {
    if (!type || type === 'off') {
      localStorage.removeItem(VIEW_AS_KEY);
      setViewAsTypeState(null);
    } else {
      localStorage.setItem(VIEW_AS_KEY, type);
      setViewAsTypeState(type);
    }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('bq_token');
    if (!token) return;
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('bq_token');
    if (!token) {
      setLoading(false);
      return;
    }
    api.get('/auth/me')
      .then(res => setUser(res.data))
      .catch(() => localStorage.removeItem('bq_token'))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('bq_token', res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, userType = 'end_user', extras = {}) => {
    const res = await api.post('/auth/register', {
      name, email, password, user_type: userType,
      ...extras,
    });
    // New verification-required flow: no token, no user set — caller shows a "check your email" screen.
    if (res.data.verification_sent) {
      return { verification_sent: true, message: res.data.message };
    }
    // Admin-seed path (first user) still gets an instant session.
    if (res.data.token) {
      localStorage.setItem('bq_token', res.data.token);
      setUser(res.data.user);
    }
    return res.data.user || { verification_sent: false };
  };

  const resendVerification = async (email) => {
    const res = await api.post('/auth/resend-verification', { email });
    return res.data;
  };

  const verifyEmail = async (token) => {
    const res = await api.post('/auth/verify-email', { token });
    if (res.data.token) {
      localStorage.setItem('bq_token', res.data.token);
      setUser(res.data.user);
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('bq_token');
    setViewAsType(null);
    setUser(null);
  };

  /** Drop-in session setter used by external auth flows (e.g. Google OAuth
   *  callback). Same shape as what /auth/login returns — token + user. */
  const setSession = useCallback((token, u) => {
    localStorage.setItem('bq_token', token);
    setUser(u);
  }, []);

  // Effective tier used for storefront pricing. Admins can preview any tier;
  // non-admins always see their real tier.
  const effectiveUserType = (user?.is_admin && viewAsType) ? viewAsType : (user?.user_type || 'end_user');

  return (
    <AuthContext.Provider value={{
      user, login, register, logout, loading, refresh,
      resendVerification, verifyEmail, setSession,
      viewAsType, setViewAsType, effectiveUserType,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
